import React, { useEffect } from 'react';
import { Modal, Button } from 'antd';
import { CheckCircle2, Calendar, Clock, MapPin, User, Wrench, Sparkles } from 'lucide-react';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';

const BookingSuccessModal = ({ visible, onClose, bookingData }) => {

  useEffect(() => {
    if (visible) {
      // Trigger fireworks effect
      const duration = 5 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);

        // Since particles fall down, start a bit higher than random
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);

      return () => clearInterval(interval);
    }
  }, [visible]);

  if (!bookingData) return null;

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      centered
      width={550}
      style={{ marginTop: 120 }}
      className="success-booking-modal"
      bodyStyle={{ padding: 0, borderRadius: 24, overflow: 'hidden' }}
    >
      <div className="relative overflow-hidden w-full max-h-[90vh] overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {/* Header Background */}
        <div className="h-20 bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center relative">
          <motion.div
            initial={{ scale: 0, rotate: -45 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', damping: 12, stiffness: 200, delay: 0.2 }}
            className="z-10 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl shadow-green-900/20"
          >
            <CheckCircle2 size={36} className="text-emerald-500" />
          </motion.div>

          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16 blur-2xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full -ml-12 -mb-12 blur-xl" />
        </div>

        <div className="p-5 pt-5 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-xl font-black text-slate-900 uppercase tracking-tight mb-1"
          >
            Đặt lịch thành công!
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-slate-500 text-xs mb-4 px-2"
          >
            Mã đặt lịch được ghi nhận. NV sẽ liên hệ xác nhận trong 30 phút.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            className="bg-slate-50 rounded-xl p-4 text-left border border-slate-100 mb-4"
          >
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <User size={12} className="text-red-500" /> Khách hàng
                </span>
                <span className="text-sm font-bold text-slate-800">{bookingData.name}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Wrench size={12} className="text-red-500" /> Dịch vụ
                </span>
                <span className="text-sm font-bold text-slate-800">{bookingData.serviceType}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Calendar size={12} className="text-red-500" /> Ngày hẹn
                </span>
                <span className="text-sm font-bold text-slate-800">{bookingData.dateDisplay}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <Clock size={12} className="text-red-500" /> Giờ hẹn
                </span>
                <span className="text-sm font-bold text-slate-800">{bookingData.timeDisplay}</span>
              </div>
              <div className="col-span-2 flex flex-col gap-1 pt-2 border-t border-slate-200 mt-1">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <MapPin size={12} className="text-red-500" /> Chi nhánh
                </span>
                <span className="text-sm font-bold text-slate-800">{bookingData.branch}</span>
                <span className="text-[10px] text-slate-400 italic">{bookingData.branchAddress}</span>
              </div>
            </div>
          </motion.div>

          <Button
            type="primary"
            size="large"
            onClick={onClose}
            className="w-full h-12 bg-slate-900 hover:bg-red-600 border-none rounded-xl text-white font-black uppercase tracking-widest text-xs transition-all duration-300 shadow-xl shadow-slate-950/20"
          >
            Tuyệt vời, cảm ơn!
          </Button>

          <div className="mt-3 flex items-center justify-center gap-2 text-red-600">
            <Sparkles size={14} />
            <span className="text-[10px] font-black uppercase tracking-widest">Hẹn gặp lại quý khách!</span>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default BookingSuccessModal;
